import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Download, UserX, AlertTriangle, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const DataManagementCard = ({ onLogout }) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await api.get("/user/export-data");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yourlifemap-export-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dati esportati con successo!");
      setShowExportDialog(false);
    } catch {
      toast.error("Errore nell'esportazione dei dati");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "ELIMINA") return;
    setDeleteLoading(true);
    try {
      await api.delete("/user/delete-account");
      toast.success("Account eliminato.");
      setShowDeleteAccountDialog(false);
      if (onLogout) onLogout();
    } catch {
      toast.error("Errore nell'eliminazione dell'account");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Card className="border border-border shadow-sm rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lock className="w-5 h-5" strokeWidth={1.5} />
            I Miei Dati
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Gestisci i tuoi dati personali in conformità con il GDPR.
          </p>
          <div className="space-y-2">
            <Button variant="outline" className="w-full justify-start" onClick={() => setShowExportDialog(true)} data-testid="export-data-btn">
              <Download className="w-4 h-4 mr-2" /> Esporta tutti i miei dati
            </Button>
            <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setShowDeleteAccountDialog(true)} data-testid="delete-account-btn">
              <UserX className="w-4 h-4 mr-2" /> Elimina il mio account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esporta i tuoi dati</DialogTitle>
            <DialogDescription>
              Scaricherai un file JSON con tutti i tuoi dati: spese, abitudini, umore, appuntamenti, diario, note e compiti.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Annulla</Button>
            <Button onClick={handleExport} disabled={exportLoading}>
              {exportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Esporta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Elimina account
            </DialogTitle>
            <DialogDescription>
              Questa azione è irreversibile. Tutti i tuoi dati verranno eliminati permanentemente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Per confermare, scrivi <strong>ELIMINA</strong> nel campo qui sotto:</p>
            <Input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="ELIMINA" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteAccountDialog(false); setDeleteConfirmText(""); }}>Annulla</Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleteConfirmText !== "ELIMINA" || deleteLoading}>
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserX className="w-4 h-4 mr-2" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
